/* eslint-disable no-param-reassign */
import { SponsoredProvider } from '@digital-retail/sponsored-provider-library';
import { isTottusStandalone } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { AutoCorrect } from '../../components/AutoCorrect/AutoCorrect';
import { BrandTitle as SearchTitle } from '../../components/BrandTitle/BrandTitle';
import { CategoryBanner } from '../../components/CategoryBanner/CategoryBanner';
import Facets from '../../components/Facets/Facets';
import GoogleAdComponent from '../../components/GoogleAds';
import { InterlinkingFooter } from '../../components/InterlinkingFooter/InterlinkingFooter';
import PromotedFilters from '../../components/PromotedFilters/PromotedFilters';
import { RelatedKeywords } from '../../components/RelatedKeywords/RelatedKeywords';
import SelectedFilters from '../../components/SelectedFilters/SelectedFilters';
import SponsorDisplayLeftWrapper from '../../components/SponsorDisplayLeftWrapper';
import SurveyBar from '../../components/SurveyBar/SurveyBar';
import MatchMedia from '../../components/ui/MatchMedia/MatchMedia';
import { constants } from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withCart } from '../../utils/CartContext';
import { useShouldHideSponsoredDisplay } from '../../utils/hooks/UseShouldHideSponsoredPod';
import { isInterlinkingEnabled } from '../../utils/interlinking/helpers/toggles';
import { scrollToTopProductList } from '../../utils/scrollToProductList';
import SearchResults from '../SearchResults/SearchResults';
import {
  getAdInjectorEnabled,
  getFacetParams,
  getHandleBannerActionForSponsorDisplay,
  getIsSponsoredDisplayEnabled,
  getPageId,
  getUrlBase,
} from '../SearchResults/utils';
import { SearchLandingContainerStyles } from './SearchLandingContainer.style';

const { publicRuntimeConfig } = getConfig();

const SearchLandingContainer = ({
  layout,
  products,
  facets,
  promotedFilter,
  appCtx,
  appliedFacets,
  availabilityFacets,
  baseFacetsURL,
  autoCorrections,
  keywordRelatedBannerHtml,
  pagination,
  sortOptions,
  searchTerm,
  relatedKeywords,
  cart,
  metadata,
  isShopPage,
  pageName,
  leftPromotionContent,
}) => {
  const router = useRouter();
  const isDesktop = appCtx.deviceType === 'desktop';
  const labels = appCtx?.siteConfig?.textDictionary ?? {};
  const productListRef = useRef();
  const [stopScroll, setStopScroll] = useState(null);
  const handleScrollToTopProductList = (selectedURLs) => {
    if (!stopScroll) {
      scrollToTopProductList(selectedURLs, productListRef, appCtx.deviceType);
    }
  };

  const visibleFilters = appliedFacets.filter((f) => f.visible);

  const selectedFiltersLabel = labels.SELECTED_FILTERS;
  const resultsLabel = labels.RESULTS;

  const Ntt = router?.query?.Ntt || '';
  const name = router?.query?.name || '';
  const providedParamforBanner = getPageId('search');
  const isSponsoredDisplayEnabled = getIsSponsoredDisplayEnabled(appCtx, Ntt);
  const adInjectorIsEnabled = getAdInjectorEnabled(appCtx);
  const [hasImpressionBeenSent, setHasImpressionBeenSent] = useState(false);
  const [showFacets, setShowFacets] = useState(true);
  const { displayName, vectorSearchApplied = false } = metadata;
  const facetParams = getFacetParams();
  const configValue = {
    regionCode: appCtx.regionCode,
    deviceType: appCtx.deviceType,
    zones: appCtx.zones,
    site: appCtx.store,
    store: appCtx.store,
    urlBase: getUrlBase(publicRuntimeConfig),
    facetParams,
    providedId: Ntt,
    providedParam: providedParamforBanner,
    categoryName: name,
    politicalId: appCtx.politicalId,
    priceGroupId: appCtx.priceGroupId,
    isOrchestratorEnabled: appCtx?.siteConfig?.toggles?.isOrchestratorEnabled ?? false,
    toggles: {
      disableSponsoredDisplay: appCtx?.siteConfig?.toggles?.disableSponsoredDisplay ?? false,
      disableSponsoredBrands: appCtx?.siteConfig?.toggles?.disableSponsoredBrands ?? false,
      disableSponsoredProducts: appCtx?.siteConfig?.toggles?.disableSponsoredProducts ?? false,
      disableSponsoredStrip: appCtx?.siteConfig?.toggles?.disableSponsoredStrip ?? false,
      disableSponsoredCarousel: appCtx?.siteConfig?.toggles?.disableSponsoredCarousel ?? false,
      disableSponsoredProductBanner: appCtx?.siteConfig?.toggles?.disableSponsoredProductBanner ?? false,
    },
  };

  const onFacetChange = () => {
    setStopScroll(false);
  };

  useEffect(() => {
    const eventToDigitalData = new CustomEvent('DDXLPRelatedKeywordDelete', {
      bubbles: true,
      detail: {
        payload: searchTerm,
      },
    });
    window.dispatchEvent(eventToDigitalData);
  }, [searchTerm]);

  useEffect(() => {
    if (window?.digitalData?.tag?.XLP) {
      window.digitalData.tag.XLP.vectorSearchApplied = vectorSearchApplied;
    }
  }, [vectorSearchApplied]);

  const getRelatedKeywords = () => {
    return (
      relatedKeywords.length > 0 && (
        <div className="container">
          <RelatedKeywords
            keywords={relatedKeywords}
            searchTerm={searchTerm}
            heading={constants.RELATED_KEYWORDS_HEADING}
          />
        </div>
      )
    );
  };

  const handleBannerAction = getHandleBannerActionForSponsorDisplay({
    appCtx,
    config: publicRuntimeConfig,
    categoryName: name,
    categoryId: undefined,
    mPos: 10,
    hasImpressionBeenSent,
    setHasImpressionBeenSent,
  });
  const shouldHideSDForKiosk = useShouldHideSponsoredDisplay(appCtx);
  const desktop = () => {
    if (visibleFilters.length) {
      return (
        <SelectedFilters
          filters={visibleFilters}
          appCtx={appCtx}
          baseFacetsURL={baseFacetsURL}
          selectedFiltersLabel={selectedFiltersLabel}
          facets={facets}
          pageName={pageName}
        />
      );
    }

    return null;
  };

  return (
    <SponsoredProvider config={configValue}>
      <div className="pod-group" data-vector-search={vectorSearchApplied}>
        {adInjectorIsEnabled && !keywordRelatedBannerHtml && (
          <div className="google-ad-top">
            <GoogleAdComponent adsPosition={constants.GOOGLE_AD_POSITION.TOP} deviceType={isDesktop} />
          </div>
        )}
        {keywordRelatedBannerHtml && (
          <div className="pod-group--keyword-related-banner container">
            <CategoryBanner categoryBannerHtml={keywordRelatedBannerHtml} store={appCtx.store} />
          </div>
        )}
        {autoCorrections.length !== 0 && (
          <div className="pad-group--auto-correct-mkp container">
            <AutoCorrect searchTerm={searchTerm} items={autoCorrections} appCtx={appCtx} />
          </div>
        )}
        <div className="pod-group--container container">
          <section className="pod-group--filters">
            {isShopPage && (
              <SearchTitle
                brandTitle={displayName}
                resultsLabel={resultsLabel}
                paginationCount={pagination.data.count}
                deviceType={appCtx.deviceType}
                isShopPage
              />
            )}
            <MatchMedia deviceType={appCtx.deviceType} content={{ desktop }} />
            {showFacets && (
              <Facets
                availabilityFacets={availabilityFacets}
                baseFacetsURL={baseFacetsURL}
                sortOptions={sortOptions}
                data={facets}
                onFacetChange={onFacetChange}
                handleScrollToTopProductList={handleScrollToTopProductList}
                appliedFacets={appliedFacets}
                pagination={pagination}
                pageName={pageName}
              />
            )}
            {!isTottusStandalone(appCtx.store) && (
              <div className="survey-container">
                <SurveyBar withBorder />
              </div>
            )}
            <div className="vertical-banner">
              {isSponsoredDisplayEnabled && isDesktop && !shouldHideSDForKiosk && (
                <SponsorDisplayLeftWrapper handleBannerAction={handleBannerAction} appCtx={appCtx} />
              )}
            </div>
            {adInjectorIsEnabled && isDesktop && (
              <div className="google-ad-side">
                <GoogleAdComponent adsPosition={constants.GOOGLE_AD_POSITION.SIDE} deviceType={isDesktop} />
              </div>
            )}
          </section>
          <section className="pod-group--products" ref={productListRef}>
            {promotedFilter && (
              <PromotedFilters promotedFilter={promotedFilter} baseFacetsURL={baseFacetsURL} pageName={pageName} />
            )}
            <SearchResults
              layout={layout}
              products={products}
              pagination={pagination}
              sortOptions={sortOptions}
              tenant={appCtx.tenant}
              pageName={pageName}
              cart={cart}
              setShowFacets={setShowFacets}
              setStopScroll={setStopScroll}
              relatedKeywordsUI={getRelatedKeywords()}
              leftPromotionContent={leftPromotionContent}
            />
          </section>
        </div>

        {!isTottusStandalone(appCtx.store) && (
          <div className="survey-container-mobile">
            <SurveyBar />
          </div>
        )}

        <style jsx>{SearchLandingContainerStyles}</style>
      </div>
      <InterlinkingFooter
        title={appCtx?.interlinking?.title || ''}
        groups={appCtx?.interlinking?.groups || []}
        isEnabled={isInterlinkingEnabled(appCtx?.siteConfig?.toggles)}
      />
    </SponsoredProvider>
  );
};

SearchLandingContainer.defaultProps = {
  products: [],
  facets: [],
  appliedFacets: [],
  searchTerm: '',
  keywordRelatedBannerHtml: '<div></div>',
  layout: {
    desktop: {
      view: '4_GRID',
    },
    mobile: {
      view: '2_GRID',
    },
  },
  relatedKeywords: [],
  metadata: {},
  isShopPage: false,
  pageName: 'search',
  leftPromotionContent: '',
};

SearchLandingContainer.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  facets: PropTypes.arrayOf(PropTypes.object),
  appliedFacets: PropTypes.arrayOf(PropTypes.object),
  appCtx: PropTypes.object.isRequired,
  pagination: PropTypes.shape({
    data: PropTypes.object,
    onPageSelection: PropTypes.func,
  }).isRequired,
  sortOptions: PropTypes.array.isRequired,
  searchTerm: PropTypes.string,
  baseFacetsURL: PropTypes.string.isRequired,
  autoCorrections: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  layout: PropTypes.object,
  relatedKeywords: PropTypes.array,
  promotedFilter: PropTypes.object.isRequired,
  keywordRelatedBannerHtml: PropTypes.string,
  cart: PropTypes.object.isRequired,
  metadata: PropTypes.object,
  isShopPage: PropTypes.bool,
  pageName: PropTypes.string,
  leftPromotionContent: PropTypes.string,
};

export default withApplicationContext(withCart(SearchLandingContainer));
export { SearchLandingContainer };
